import { getSelectedLocale } from 'utils/getLocaleId'
import { ProductELabelIngredientDisplayVariant } from 'api/schemas'

export const formatWebsite = value => {
  if (!value || typeof value !== 'string') return value
  return value.startsWith('http') ? value : `https://${value}`
}

type FormatPriceOptions = {
  defaultValue?: string
  fractionDigits?: number
}

/**
 * https://wwl.atlassian.net/wiki/spaces/WWLFW/pages/2243690497/NFR-1
 *
 * @deprecated Use formatNumberAsPrice instead
 * @param n
 * @param options
 */
export const formatPrice = (n, options: FormatPriceOptions = {}) => {
  const { defaultValue = '-', fractionDigits } = options

  if (!n) return defaultValue
  if (typeof n !== 'number') return n

  const userLocale = getSelectedLocale()

  const formatOptions: Intl.NumberFormatOptions = { maximumFractionDigits: 2 }
  if (fractionDigits) {
    formatOptions.minimumFractionDigits = fractionDigits
    formatOptions.maximumFractionDigits = fractionDigits
  }

  const formattedPrice = n.toLocaleString('ru', formatOptions)

  return userLocale === 'en' ? formattedPrice.replace(',', '.') : formattedPrice
}

export const formatNumber = (
  value?: number | null,
  options: Intl.NumberFormatOptions = {}
) => {
  if (!value && value !== 0) return undefined

  return new Intl.NumberFormat(getSelectedLocale(), {
    style: 'decimal',
    maximumFractionDigits: 2,
    ...options,
  }).format(value)
}

export const formatNumberAsPrice = (
  value?: number | string,
  options: Intl.NumberFormatOptions & { defaultValue?: string } = {}
) => {
  const { defaultValue = '-', ...rest } = options
  if (!value) return defaultValue
  return formatNumber(
    typeof value === 'string' ? +value.replace(',', '.') : value,
    rest
  )
}

export const numberWithSpaces = x => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

export const formatBytes = (sizeInBytes, decimals = 2) => {
  if (!+sizeInBytes) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(sizeInBytes) / Math.log(k))

  return `${parseFloat((sizeInBytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export const formatIngredient = (
  item,
  showVariant: ProductELabelIngredientDisplayVariant = ProductELabelIngredientDisplayVariant.Name
) => {
  if (typeof item === 'string') return item
  if (ProductELabelIngredientDisplayVariant.Number === showVariant) {
    return item?.code ? `[${item?.code}]` : ''
  }
  const showCode =
    showVariant === ProductELabelIngredientDisplayVariant.NameNumber
  const code = `${item?.code && showCode ? ` [${item.code}]` : ''}`
  const ingredient = `${item?.localizedName ?? ''}`
  return item?.category?.isPrintable
    ? `${item?.category?.localizedName}: ${ingredient.toLowerCase()}${code}`
    : `${ingredient}${code}`
}

export const toFixedFractionDigits = (
  num: number,
  fractionDigits: number = 2
): number => {
  const n = Math.pow(10, fractionDigits)
  return Math.round(num * n) / n
}

export const formatCurrency = (symbol: string) =>
  symbol?.length >= 3 ? `${symbol} ` : symbol

const NBSP = ' '

export type PricePlacement = 'left' | 'right'

export const formatPriceWithCurrency = (
  value: number | string | undefined | null,
  currency: string,
  placement: PricePlacement,
  options: { defaultValue?: string } = {}
): string => {
  const { defaultValue = '-' } = options

  if (value === null || value === undefined || value === '') return defaultValue

  const num = typeof value === 'string' ? +value.replace(',', '.') : value
  if (isNaN(num)) return defaultValue

  const locale = getSelectedLocale()

  let formatted: string
  if (num > 0 && num < 0.01) {
    const decimalSeparator = new Intl.NumberFormat(locale)
      .format(1.1)
      .replace(/1/g, '')
    formatted = `<0${decimalSeparator}01`
  } else {
    formatted = new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num)
  }

  return placement === 'left'
    ? `${currency}${NBSP}${formatted}`
    : `${formatted}${NBSP}${currency}`
}
